import { useEffect, useRef, useState } from 'react'
import { api } from '../api'
import { DownloadIcon } from '../icons'
import { Spinner } from '../ui'
import { cls } from '../util'

/*
  Download what's on screen, per camera: the clip as shot (its 1080p transcode, log) or with the
  project's look baked in (rendered on request by a runner with the player's own look code, so it
  matches the picture here). Both keep the transcode's name and timecode, so either swaps 1:1 in an
  edit. Shown to the team, and to clients whose link allows downloads.
*/
export type DownloadClip = { label: string; mediaId: string; name: string }
type Ready = { size: number; url: string; file?: string }
type Downloads = {
  name: string
  shot: Ready | null
  look: { kind: string; label: string; film: boolean; camera: string; ready: Ready | null; job: { status: string; progress: number; error: string | null } | null } | null
}

const size = (b: number) => (b >= 1e9 ? `${(b / 1e9).toFixed(1)} GB` : `${Math.max(1, Math.round(b / 1e6))} MB`)

function ClipRows({ projectId, clip, open }: { projectId: string; clip: DownloadClip; open: boolean }) {
  const [d, setD] = useState<Downloads | null>(null)
  const [err, setErr] = useState('')
  const [asking, setAsking] = useState(false)
  const path = `p/${projectId}/media/${clip.mediaId}`
  const busy = !!d?.look?.job && ['queued', 'running'].includes(d.look.job.status) && !d.look.ready
  useEffect(() => {
    if (!open) return
    let live = true
    let timer = 0
    const load = async () => {
      try {
        const r = await api.get<Downloads>(`${path}/downloads`)
        if (!live) return
        setD(r)
        setErr('')
        if (r.look?.job && ['queued', 'running'].includes(r.look.job.status) && !r.look.ready) timer = window.setTimeout(load, 3000)
      } catch (e) {
        if (live) setErr((e as Error).message)
      }
    }
    load()
    return () => {
      live = false
      clearTimeout(timer)
    }
  }, [open, path, busy])
  const make = async () => {
    setAsking(true)
    try {
      await api.post(`${path}/look`)
      setD(await api.get<Downloads>(`${path}/downloads`))
    } catch (e) {
      setErr((e as Error).message)
    }
    setAsking(false)
  }
  const look = d?.look
  const job = look?.job
  return (
    <div className="dl-clip">
      <div className="dl-head"><b>{clip.label} cam</b><span className="mono">{clip.name}</span></div>
      {!d && !err && <div className="dl-row"><Spinner small /></div>}
      {err && <p className="dl-err">{err}</p>}
      {d && (
        <>
          <div className="dl-row">
            <div className="dl-what"><b>as shot</b><span>{d.shot ? `log, 1080p · ${size(d.shot.size)}` : 'the transcode isn’t made yet'}</span></div>
            {d.shot && <a className="dl-get" href={d.shot.url} download={d.shot.file || ''} aria-label={`Download ${clip.label} cam as shot`}><DownloadIcon size={14} /></a>}
          </div>
          <div className="dl-row">
            <div className="dl-what">
              <b>with look</b>
              <span>
                {!look ? 'pick a look first'
                  : look.ready ? `${look.label} · ${size(look.ready.size)}`
                  : !d.shot ? `${look.label} · needs the transcode first`
                  : job?.status === 'running' ? `${look.label} · rendering ${Math.round((job.progress || 0) * 100)}%`
                  : job?.status === 'queued' ? `${look.label} · waiting for a machine`
                  : job?.status === 'failed' ? `${look.label} · that didn’t work${job.error ? `: ${job.error}` : ''}`
                  : look.label}
              </span>
              {job?.status === 'running' && !look?.ready && <div className="bar dl-bar"><div style={{ width: `${Math.max(2, (job.progress || 0) * 100)}%` }} /></div>}
            </div>
            {look?.ready ? (
              <a className="dl-get" href={look.ready.url} download={look.ready.file || ''} aria-label={`Download ${clip.label} cam with look`}><DownloadIcon size={14} /></a>
            ) : look && d.shot && !busy ? (
              <button className="dl-make" onClick={make} disabled={asking}>{job?.status === 'failed' ? 'try again' : 'make it'}</button>
            ) : busy ? <Spinner small /> : null}
          </div>
        </>
      )}
    </div>
  )
}

export function DownloadMenu({ projectId, clips }: { projectId: string; clips: DownloadClip[] }) {
  const [open, setOpen] = useState(false)
  const ref = useRef<HTMLDivElement>(null)
  useEffect(() => {
    if (!open) return
    const off = (e: MouseEvent) => !ref.current?.contains(e.target as Node) && setOpen(false)
    const esc = (e: KeyboardEvent) => e.key === 'Escape' && setOpen(false)
    window.addEventListener('mousedown', off)
    window.addEventListener('keydown', esc)
    return () => {
      window.removeEventListener('mousedown', off)
      window.removeEventListener('keydown', esc)
    }
  }, [open])
  if (!clips.length) return null
  return (
    <div className="dl-menu" ref={ref}>
      <button className={cls('ctl-dl', open && 'on')} onClick={() => setOpen((o) => !o)} aria-expanded={open} aria-label="Download" title="Download this clip">
        <DownloadIcon size={15} />
      </button>
      {open && (
        <div className="dl-pop" role="dialog" aria-label="Download">
          <h4>download</h4>
          {clips.map((c) => <ClipRows key={c.label + c.mediaId} projectId={projectId} clip={c} open={open} />)}
          <p className="dl-foot">Same name and timecode as the transcode, so either one swaps 1:1 in an edit.</p>
        </div>
      )}
    </div>
  )
}
